import { useEffect, useRef, useReducer } from 'react'
import { Outlet, Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { Header } from './Header'

const SCROLLBAR_VISIBLE_MS = 1000

type ScrollState = { isScrolling: boolean }
type ScrollAction = { type: 'START' } | { type: 'END' }
function scrollReducer(state: ScrollState, action: ScrollAction): ScrollState {
  switch (action.type) {
    case 'START':
      return { isScrolling: true }
    case 'END':
      return { isScrolling: false }
    default:
      return state
  }
}

export function MainLayout() {
  const { user, isInitialized } = useAuthStore()
  const location = useLocation()
  const mainRef = useRef<HTMLElement>(null)
  const [scrollState, dispatchScroll] = useReducer(scrollReducer, { isScrolling: false })
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isPageScrollRoute = location.pathname.includes('/settings')
  const isScrolling = scrollState.isScrolling

  useEffect(() => {
    document.documentElement.dataset.pageScroll = isPageScrollRoute ? 'true' : 'false'
    return () => {
      delete document.documentElement.dataset.pageScroll
    }
  }, [isPageScrollRoute])

  useEffect(() => {
    const el = isPageScrollRoute ? window : mainRef.current
    if (!el) return

    const handleScroll = () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
      dispatchScroll({ type: 'START' })
      scrollTimeoutRef.current = setTimeout(() => {
        dispatchScroll({ type: 'END' })
        scrollTimeoutRef.current = null
      }, SCROLLBAR_VISIBLE_MS)
    }

    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      el.removeEventListener('scroll', handleScroll)
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
    }
  }, [isPageScrollRoute])

  if (!isInitialized) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="w-8 h-8 rounded-full border-b-2 animate-spin border-foreground"></div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return (
    <div
      className={`flex flex-col min-w-0 bg-background text-foreground ${isPageScrollRoute ? 'min-h-dvh' : 'overflow-hidden h-dvh min-h-0'}`}
    >
      <Header />
      <main
        ref={mainRef}
        className={`flex flex-col flex-1 min-w-0 overflow-x-hidden px-4 pt-18 pb-4 w-full sm:pt-6 sm:pb-6 sm:px-6 ${isPageScrollRoute ? 'min-h-0' : `min-h-0 overflow-y-auto scrollbar-auto ${isScrolling ? 'is-scrolling' : ''}`}`}
      >
        <div className={`flex flex-col min-w-0 ${isPageScrollRoute ? 'flex-1 min-h-0' : 'flex-1 min-h-full'}`}>
          <Outlet key={location.pathname} />
        </div>
      </main>
    </div>
  )
}
